import { useState, useMemo, useCallback } from "react";
import { Search, X, BookOpen } from "lucide-react";
import { GLOSSARY, GLOSSARY_CATEGORIES } from "../glossaryData";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { VoiceButton } from "../components/VoiceButton";
import { SpeakButton } from "../components/SpeakButton";
import { CTABand } from "../components/CTABand";

export default function Glossary() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const handleVoice = useCallback((text) => setQuery(text), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GLOSSARY.filter((g) => {
      const okCat = category === "All" || g.category === category;
      const okQ =
        !q ||
        g.term.toLowerCase().includes(q) ||
        g.definition.toLowerCase().includes(q);
      return okCat && okQ;
    }).sort((a, b) => a.term.localeCompare(b.term));
  }, [query, category]);

  return (
    <div data-testid="glossary-page">
      <PageHero
        eyebrow="Plain‑English Dictionary"
        title="Tech Glossary"
        subtitle="Confused by a word you heard? Look it up here. Every term is explained in simple, everyday language — no tech talk. You can even tap the microphone and just say the word."
      />

      {/* Search + voice */}
      <section className="bg-card sticky top-20 z-30 border-b border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground" />
              <input
                data-testid="glossary-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a word, e.g. router, cloud, password…"
                className="h-14 w-full rounded-full border-2 border-border bg-card pl-14 pr-12 text-lg outline-none focus:border-accent transition-colors"
              />
              {query && (
                <button
                  data-testid="glossary-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 grid place-items-center h-9 w-9 rounded-full text-muted-foreground hover:bg-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <VoiceButton
              onResult={handleVoice}
              idleLabel="Tap to speak"
              testId="glossary-voice"
            />
          </div>

          {/* Category chips */}
          <div className="mt-4 flex gap-2 flex-wrap">
            {GLOSSARY_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`glossary-cat-${c}`}
                onClick={() => setCategory(c)}
                className={`h-11 px-5 rounded-full font-semibold transition-colors ${
                  category === c
                    ? "bg-primary text-white"
                    : "bg-secondary border border-border text-slate-300 hover:border-accent"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <p className="text-muted-foreground mb-8 text-lg" data-testid="glossary-count">
          Showing <strong className="text-foreground">{results.length}</strong>{" "}
          {results.length === 1 ? "term" : "terms"}
          {query && <> for “{query}”</>}
        </p>

        {results.length === 0 ? (
          <div data-testid="glossary-empty" className="rounded-2xl border border-dashed border-border bg-card p-16 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="font-heading font-bold text-2xl text-foreground">No matches found</p>
            <p className="mt-2 text-muted-foreground text-lg">
              Try a different word — or give us a call and we'll happily explain it.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {results.map((g, i) => (
              <Reveal key={g.term} delay={(i % 2) * 0.05} y={16}>
                <div
                  data-testid={`glossary-term-${i}`}
                  className="h-full rounded-2xl border border-border bg-card p-8 hover:border-accent/40 hover:shadow-lg transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h3 className="font-heading font-bold text-2xl text-foreground">{g.term}</h3>
                    <span className="rounded-full bg-accent/10 text-accent text-xs font-bold px-3 py-1 whitespace-nowrap">
                      {g.category}
                    </span>
                  </div>
                  <p className="text-slate-300 text-lg leading-relaxed">{g.definition}</p>
                  <div className="mt-5">
                    <SpeakButton
                      text={`${g.term}. ${g.definition}`}
                      testId={`glossary-speak-${i}`}
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <CTABand />
    </div>
  );
}
